/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CompletionMessage, FileUpload } from '@app-libs/model';
import { MarkdownModule, MermaidAPI } from 'ngx-markdown';
import { EventBusService, EventTypes } from '../../../services/client/event-bus.service';
import { Subscription, takeUntil, Subject } from 'rxjs';
import { ChatService } from '../../../services/chat/chat.service';
import { Router } from '@angular/router';
import {
  filterSystemMessages,
  isNavigationLink,
  extractNavigationRoute,
} from '../../../utils/message-format.utils';
import { TypingIndicatorComponent } from './typing-indicator/typing-indicator.component';
import { MessageContainerComponent } from './message-container/message-container.component';
import { MessageFormatter } from '../../../services/chat/message/message-formatter.service';
import { StreamProcessor } from '../../../services/chat/streaming/stream-processor.service';

@Component({
  selector: 'app-chat-messages',
  imports: [CommonModule, MarkdownModule, TypingIndicatorComponent, MessageContainerComponent],
  templateUrl: './chat-messages.component.html',
  styleUrl: './chat-messages.component.scss',
  // Removed OnPush for streaming compatibility
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessagesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() messages: Partial<CompletionMessage>[] = [];
  @Input() welcomeMessage = '';

  showWelcomeMessage = false;
  showTypingIndicator = false;
  typingMessage = 'Thinking';
  activeMessageId: string | null = null;
  copiedMessageId = '';
  exportedMessageId = '';
  isCopyClicked = false;

  private subscriptions: Subscription = new Subscription();
  private destroy$ = new Subject<void>();

  // Cache for filtered messages to prevent re-computation
  private _cachedFilteredMessages: CompletionMessage[] = [];
  private _lastMessages: Partial<CompletionMessage>[] = [];

  public mermaidOptions: MermaidAPI.MermaidConfig = {
    logLevel: 'fatal',
    suppressErrorRendering: true,
  };

  constructor(
    private router: Router,
    private eventBus: EventBusService,
    private chatService: ChatService,
    private cdr: ChangeDetectorRef,
    private messageFormatter: MessageFormatter,
    private streamProcessor: StreamProcessor,
  ) {}

  ngOnInit() {
    this.subscribeToEvent(EventTypes.FILE_UPLOADED, this.onFileUploaded.bind(this));
    this.subscribeToEvent(EventTypes.REMOVE_UPLOADED_FILE, this.onFileRemoved.bind(this));
    this.subscribeToEvent(
      EventTypes.STREAM_FIRST_CHUNK_RECEIVED,
      this.onStreamChunkReceived.bind(this),
    );
    this.subscribeToEvent(EventTypes.SEND_USER_MESSAGE, this.onSendUserMessage.bind(this));
    this.subscribeToEvent(EventTypes.STREAM_COMPLETE, this.onStreamComplete.bind(this));
    // Scroll is now handled by ChatScrollService
    this.subscribeToEvent(EventTypes.NEW_CHAT, this.onNewChat.bind(this));
  }

  private subscribeToEvent(eventType: EventTypes, handler: (data: any) => void) {
    this.subscriptions.add(this.eventBus.on(eventType).subscribe((event) => handler(event.data)));
  }

  onStreamComplete(isError?: boolean) {
    this.showTypingIndicator = false;
    if (this.showWelcomeMessage) {
      this.showWelcomeMessage = false;
    }
  }

  get state() {
    return this.chatService.state;
  }

  get filteredMessages(): CompletionMessage[] {
    // Only recompute if messages array reference has changed
    if (this.messages !== this._lastMessages) {
      this._lastMessages = this.messages;
      this._cachedFilteredMessages = filterSystemMessages(this.messages);
    }
    return this._cachedFilteredMessages;
  }

  /**
   * Check if a message has math content using MessageFormatter
   */
  hasMessageMathContent(message: CompletionMessage): boolean {
    return this.messageFormatter.hasMathContent(message);
  }

  /**
   * Check if a message has code blocks using MessageFormatter
   */
  hasMessageCodeBlocks(message: CompletionMessage): boolean {
    return this.messageFormatter.hasCodeBlocks(message);
  }

  /**
   * Get formatted message for display using MessageFormatter
   */
  getFormattedMessage(message: CompletionMessage) {
    return this.messageFormatter.formatForDisplay(message);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['messages']) {
      this.updateShowWelcomeMessage();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateShowWelcomeMessage() {
    this.showWelcomeMessage = this.filteredMessages.length === 0;
  }

  onSendUserMessage(data: any) {
    this.showTypingIndicator = true;
    if (this.showWelcomeMessage) {
      this.showWelcomeMessage = false;
    }
    this.typingMessage = 'Thinking';
  }

  onStreamChunkReceived(data: any) {
    this.showTypingIndicator = false;
  }

  onFileUploaded(file: FileUpload | undefined) {
    // File upload handling is managed by services
  }

  onFileRemoved(file: FileUpload | undefined) {
    // File removal handling is managed by services
  }

  removeFile(file: FileUpload | undefined) {
    // Delegate to parent component or service
    this.eventBus.emit({ type: EventTypes.REMOVE_UPLOADED_FILE, data: file });
  }

  onCopySuccess(message: CompletionMessage) {
    this.copiedMessageId = message.message_id || '';
  }

  onExportSuccess(message: CompletionMessage) {
    this.exportedMessageId = message.message_id || '';
    setTimeout(() => {
      this.exportedMessageId = '';
    }, 2000);
  }

  onExportError(event: { message: CompletionMessage; error: string }) {
    console.error('PDF export failed:', event.error);
    // TODO: Add proper error notification
  }

  onMessageEnter(messageId: string | null) {
    this.activeMessageId = messageId;
    this.cdr.detectChanges();
  }

  onMessageLeave() {
    if (this.isCopyClicked) {
      return;
    }
    this.activeMessageId = null;
  }

  onMessageClick(message?: CompletionMessage) {
    // Handle message click if needed
  }

  onMessageMouseDown(message?: CompletionMessage) {
    this.isCopyClicked = true;
  }

  onMessageMouseUp(message?: CompletionMessage) {
    this.isCopyClicked = false;
  }

  handleWelcomeClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (isNavigationLink(target)) {
      event.preventDefault();
      const route = extractNavigationRoute(target.getAttribute('href') || '');
      if (route) {
        this.router.navigate([route]);
      }
    }
  }

  onNewChat(data: any) {
    this.updateShowWelcomeMessage();
  }
}
